import moment from 'moment-timezone';
import { type KnexPaginateArgs } from '../types/knex-paginate';
import { AiToolFriendlyMessage } from '../types/templates';
import { Timezones } from '../types/timezone';

export const generateFriendlyLabel = (str: string, delimiter: string) => {
    if (!str.length) {
        return str;
    }

    const newStr = str.replace('srt_', '');
    const parts = newStr.split(delimiter);
    parts[0] = parts[0]
        ? `${parts[0].charAt(0).toUpperCase()}${parts[0]
              .slice(1)
              .toLowerCase()}`
        : '';
    return parts.join(' ');
};

export const generateColumnNameByLabel = (
    str: string,
    splitDelimiter: string,
    joinDelimiter: string,
) => {
    if (!str.length) {
        return str;
    }
    const parts = str.split(splitDelimiter);
    const columnName = parts
        .map((part) => part.toLowerCase())
        .join(joinDelimiter);
    return columnName;
};

/**
 * Returns a promise that resolves after a given number of milliseconds
 * @param ms - number of milliseconds to wait
 * @returns a promise that resolves after the given number of milliseconds
 * @example
 * ```typescript
 * await delay(1000);
 * ```
 */
export const delay = (ms: number) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

/**
 * Calculates an exponential backoff time based on the number of failures.
 *
 * @param {number} failureCount - The number of consecutive failures or retries.
 * @param {number} [baseTime=1000] - The base time in milliseconds. The initial delay before the first retry.
 * @param {number} [factor=2] - The exponential factor. Determines the growth rate of the delay after each failure.
 * @param {number} [maxBackoffTime=Number.MAX_VALUE] - The maximum allowable backoff time in milliseconds.
 *
 * @returns {number} - The calculated backoff time in milliseconds, capped at maxBackoffTime.
 */
export const calculateBackoffTime = (
    failureCount: number,
    maxBackoffTime: number = Number.MAX_VALUE, // Max backoff time (default: Number.MAX_VALUE)
    baseTime: number = 1000, // Base time in milliseconds (default: 1 second)
    factor: number = 2, // Exponential factor (default: 2)
): number => {
    // Calculate backoff time using exponential backoff formula: baseTime * (factor^failureCount)
    const backoffTime = baseTime * factor ** failureCount;

    // Return the minimum of the calculated backoff time or the maxBackoffTime
    return Math.min(backoffTime, maxBackoffTime);
};

export const generateLeafJsonPaths = (
    data: Record<string, unknown>,
    ignoreArrays: boolean,
    currentPath: string = '$',
): string[] => {
    const paths: string[] = [];

    if (typeof data === 'object' && data !== null) {
        if (Array.isArray(data) && !ignoreArrays) {
            // If the current data is an array
            data.forEach((item, index) => {
                const arrayPath = `${currentPath}[${index}]`;
                paths.push(
                    ...generateLeafJsonPaths(
                        item as Record<string, unknown>,
                        ignoreArrays,
                        arrayPath,
                    ),
                );
            });
        } else {
            // If the current data is an object
            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    const objectPath = `${currentPath}.${key}`;
                    paths.push(
                        ...generateLeafJsonPaths(
                            data[key] as Record<string, unknown>,
                            ignoreArrays,
                            objectPath,
                        ),
                    );
                }
            }
        }
    } else {
        // If it's a leaf node (not an object or array), add the current path
        paths.push(currentPath);
    }

    return paths;
};

export function convertLDPaginationToSortmentPagination(
    ldPagination: KnexPaginateArgs & {
        totalPageCount: number;
        totalResults: number;
    },
): {
    total?: number;
    lastPage?: number;
    currentPage: number;
    perPage: number;
    from: number;
    to: number;
} {
    return {
        total: ldPagination.totalResults,
        lastPage: ldPagination.totalPageCount,
        currentPage: ldPagination.page,
        perPage: ldPagination.pageSize,
        from: (ldPagination.page - 1) * ldPagination.pageSize + 1,
        to: ldPagination.page * ldPagination.pageSize,
    };
}

export const isValidUrl = (string: string) => {
    try {
        return Boolean(new URL(string));
    } catch (_) {
        return false;
    }
};

export const validateHandlebarVariable = (variable: string) => {
    const regex = /^[a-zA-Z0-9]+$/g;

    return regex.test(variable);
};

/**
 * Converts a cron string from workspace timezone to UTC timezone
 * This assumes the cron string was created in the workspace timezone
 * and needs to be converted to UTC for backend processing
 *
 * Example:
 * If workspace is in EST (UTC-5) and cron is "0 9 * * *" (9 AM EST):
 * This converts to "0 14 * * *" (2 PM UTC) to maintain the same execution time
 *
 * @param cronString - Cron string in workspace timezone (e.g. "0 9 * * *")
 * @param workspaceTimezone - The timezone string of the workspace (e.g. "America/New_York")
 * @returns Cron string adjusted for UTC timezone
 */
export const convertCronToUtc = (
    cronString: string,
    workspaceTimezone: string = Timezones.UTC,
): string => {
    // If workspace is already UTC, no conversion needed
    if (workspaceTimezone === Timezones.UTC) {
        return cronString;
    }

    // Parse the cron string components
    const cronParts = cronString.trim().split(/\s+/);

    if (cronParts.length < 5) {
        // console.error('Invalid cron string format', cronString);
        return cronString;
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = cronParts;
    const hasComplexTimePart =
        (minute !== '*' && Number.isNaN(parseInt(minute, 10))) ||
        (hour !== '*' && Number.isNaN(parseInt(hour, 10)));
    if (hasComplexTimePart) {
        // Expressions like "0 */3 * * *" are valid but cannot be shifted safely
        // with this simple parser, so preserve the original expression.
        return cronString;
    }

    // Create a reference date in workspace timezone to calculate offset
    // Use a specific date to get the timezone offset
    const referenceDate = moment.tz(workspaceTimezone);
    const utcOffset = referenceDate.utcOffset(); // Offset in minutes

    // Convert hour and minute to UTC
    // Handle wildcards (*) - they should remain as wildcards
    let utcHour: number | string = hour;
    let utcMinute: number | string = minute;

    // Handle day boundary crossing
    let dayAdjustment = 0;

    // Handle specific hour and minute
    if (hour !== '*' && minute !== '*') {
        utcHour = parseInt(hour, 10);
        utcMinute = parseInt(minute, 10);

        // Adjust for timezone offset
        const totalMinutes = utcHour * 60 + utcMinute - utcOffset;

        if (totalMinutes < 0) {
            // Crossed to previous day
            dayAdjustment = -1;
            utcHour = Math.floor((totalMinutes + 24 * 60) / 60);
            utcMinute = (totalMinutes + 24 * 60) % 60;
        } else if (totalMinutes >= 24 * 60) {
            // Crossed to next day
            dayAdjustment = 1;
            utcHour = Math.floor(totalMinutes / 60) % 24;
            utcMinute = totalMinutes % 60;
        } else {
            // Same day
            utcHour = Math.floor(totalMinutes / 60);
            utcMinute = totalMinutes % 60;
        }
    } else if (hour === '*' && minute !== '*') {
        // Handle specific minute with wildcard hour (e.g., "10 * * * *")
        // We need to adjust the minute component based on timezone offset
        const minuteValue = parseInt(minute, 10);

        // Adjust minute by the timezone offset
        let adjustedMinute = minuteValue - utcOffset;

        // Handle minute wrapping (must stay in 0-59 range)
        while (adjustedMinute < 0) {
            adjustedMinute += 60;
        }
        while (adjustedMinute >= 60) {
            adjustedMinute -= 60;
        }

        utcMinute = adjustedMinute;
        utcHour = '*'; // Keep hour as wildcard
    } else if (hour !== '*' && minute === '*') {
        // Handle specific hour with wildcard minute (e.g., "* 14 * * *")
        const hourValue = parseInt(hour, 10);

        // Convert hour to UTC, considering timezone offset
        const hourOffsetInHours = Math.floor(utcOffset / 60);
        let adjustedHour = hourValue - hourOffsetInHours;

        // Handle hour wrapping and day boundary
        if (adjustedHour < 0) {
            dayAdjustment = -1;
            adjustedHour += 24;
        } else if (adjustedHour >= 24) {
            dayAdjustment = 1;
            adjustedHour -= 24;
        }

        utcHour = adjustedHour;
        utcMinute = '*'; // Keep minute as wildcard
    }
    // If both hour and minute are wildcards, no conversion needed

    // Handle day of month adjustments
    let utcDayOfMonth = dayOfMonth;
    if (dayOfMonth !== '*' && dayAdjustment !== 0) {
        const currentDay = parseInt(dayOfMonth, 10);
        if (!Number.isNaN(currentDay)) {
            const adjustedDay = currentDay + dayAdjustment;

            // Handle month boundary crossing
            if (adjustedDay <= 0) {
                // Previous month
                utcDayOfMonth = 'L'; // Last day of previous month
            } else if (adjustedDay > 31) {
                // Next month
                utcDayOfMonth = '1'; // First day of next month
            } else {
                utcDayOfMonth = adjustedDay.toString();
            }
        }
    }

    // Handle day of week adjustments
    let utcDayOfWeek = dayOfWeek;
    if (dayOfWeek !== '*' && dayAdjustment !== 0) {
        const currentDay = parseInt(dayOfWeek, 10);
        if (!Number.isNaN(currentDay)) {
            const adjustedDay = currentDay + dayAdjustment;

            // Wrap around for day of week (0-6)
            if (adjustedDay < 0) {
                utcDayOfWeek = '6'; // Saturday
            } else if (adjustedDay > 6) {
                utcDayOfWeek = '0'; // Sunday
            } else {
                utcDayOfWeek = adjustedDay.toString();
            }
        }
    }

    // Reconstruct the cron string with UTC values
    const utcCronParts = [
        typeof utcMinute === 'string' ? utcMinute : utcMinute.toString(),
        typeof utcHour === 'string' ? utcHour : utcHour.toString(),
        utcDayOfMonth,
        month,
        utcDayOfWeek,
    ];

    return utcCronParts.join(' ');
};

/**
 * Converts a cron string from UTC timezone back to workspace timezone
 * This is the reverse operation of convertCronToUtc
 * Use this when displaying cron strings from backend in the frontend
 *
 * Example:
 * If backend stores "0 14 * * *" (2 PM UTC) and workspace is in EST (UTC-5):
 * This converts to "0 9 * * *" (9 AM EST) for frontend display
 *
 * @param utcCronString - Cron string in UTC timezone (e.g. "0 14 * * *")
 * @param workspaceTimezone - The timezone string of the workspace (e.g. "America/New_York")
 * @returns Cron string adjusted for workspace timezone
 */
export const convertCronFromUtc = (
    utcCronString: string,
    workspaceTimezone: string = Timezones.UTC,
): string => {
    // If workspace is already UTC, no conversion needed
    if (workspaceTimezone === Timezones.UTC) {
        return utcCronString;
    }

    // Parse the cron string components
    const cronParts = utcCronString.trim().split(/\s+/);

    if (cronParts.length < 5) {
        // console.error('Invalid cron string format', utcCronString);
        return utcCronString;
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = cronParts;
    const hasComplexTimePart =
        (minute !== '*' && Number.isNaN(parseInt(minute, 10))) ||
        (hour !== '*' && Number.isNaN(parseInt(hour, 10)));
    if (hasComplexTimePart) {
        // Keep complex cron expressions unchanged when timezone-safe conversion
        // cannot be determined by the current parser.
        return utcCronString;
    }

    // Create a reference date in workspace timezone to calculate offset
    // Use a specific date to get the timezone offset
    const referenceDate = moment.tz(workspaceTimezone);
    const utcOffset = referenceDate.utcOffset(); // Offset in minutes

    // Convert UTC hour and minute to workspace timezone
    // Handle wildcards (*) - they should remain as wildcards
    let workspaceHour: number | string = hour;
    let workspaceMinute: number | string = minute;

    // Handle day boundary crossing
    let dayAdjustment = 0;

    // Handle specific hour and minute
    if (hour !== '*' && minute !== '*') {
        workspaceHour = parseInt(hour, 10);
        workspaceMinute = parseInt(minute, 10);

        // Adjust for timezone offset (add offset to convert from UTC to workspace)
        const totalMinutes = workspaceHour * 60 + workspaceMinute + utcOffset;

        if (totalMinutes < 0) {
            // Crossed to previous day
            dayAdjustment = -1;
            workspaceHour = Math.floor((totalMinutes + 24 * 60) / 60);
            workspaceMinute = (totalMinutes + 24 * 60) % 60;
        } else if (totalMinutes >= 24 * 60) {
            // Crossed to next day
            dayAdjustment = 1;
            workspaceHour = Math.floor(totalMinutes / 60) % 24;
            workspaceMinute = totalMinutes % 60;
        } else {
            // Same day
            workspaceHour = Math.floor(totalMinutes / 60);
            workspaceMinute = totalMinutes % 60;
        }
    } else if (hour === '*' && minute !== '*') {
        // Handle specific minute with wildcard hour (e.g., "10 * * * *")
        // We need to adjust the minute component based on timezone offset
        const minuteValue = parseInt(minute, 10);

        // Add offset to convert from UTC to workspace timezone
        let adjustedMinute = minuteValue + utcOffset;

        // Handle minute wrapping (must stay in 0-59 range)
        while (adjustedMinute < 0) {
            adjustedMinute += 60;
        }
        while (adjustedMinute >= 60) {
            adjustedMinute -= 60;
        }

        workspaceMinute = adjustedMinute;
        workspaceHour = '*'; // Keep hour as wildcard
    } else if (hour !== '*' && minute === '*') {
        // Handle specific hour with wildcard minute (e.g., "* 14 * * *")
        const hourValue = parseInt(hour, 10);

        // Convert hour from UTC to workspace timezone, considering timezone offset
        const hourOffsetInHours = Math.floor(utcOffset / 60);
        let adjustedHour = hourValue + hourOffsetInHours;

        // Handle hour wrapping and day boundary
        if (adjustedHour < 0) {
            dayAdjustment = -1;
            adjustedHour += 24;
        } else if (adjustedHour >= 24) {
            dayAdjustment = 1;
            adjustedHour -= 24;
        }

        workspaceHour = adjustedHour;
        workspaceMinute = '*'; // Keep minute as wildcard
    }
    // If both hour and minute are wildcards, no conversion needed

    // Handle day of month adjustments
    let workspaceDayOfMonth = dayOfMonth;
    if (dayOfMonth !== '*' && dayAdjustment !== 0) {
        const currentDay = parseInt(dayOfMonth, 10);
        if (!Number.isNaN(currentDay)) {
            const adjustedDay = currentDay + dayAdjustment;

            // Handle month boundary crossing
            if (adjustedDay <= 0) {
                // Previous month
                workspaceDayOfMonth = 'L'; // Last day of previous month
            } else if (adjustedDay > 31) {
                // Next month
                workspaceDayOfMonth = '1'; // First day of next month
            } else {
                workspaceDayOfMonth = adjustedDay.toString();
            }
        }
    }

    // Handle day of week adjustments
    let workspaceDayOfWeek = dayOfWeek;
    if (dayOfWeek !== '*' && dayAdjustment !== 0) {
        const currentDay = parseInt(dayOfWeek, 10);
        if (!Number.isNaN(currentDay)) {
            const adjustedDay = currentDay + dayAdjustment;

            // Wrap around for day of week (0-6)
            if (adjustedDay < 0) {
                workspaceDayOfWeek = '6'; // Saturday
            } else if (adjustedDay > 6) {
                workspaceDayOfWeek = '0'; // Sunday
            } else {
                workspaceDayOfWeek = adjustedDay.toString();
            }
        }
    }

    // Reconstruct the cron string with workspace timezone values
    const workspaceCronParts = [
        typeof workspaceMinute === 'string'
            ? workspaceMinute
            : workspaceMinute.toString(),
        typeof workspaceHour === 'string'
            ? workspaceHour
            : workspaceHour.toString(),
        workspaceDayOfMonth,
        month,
        workspaceDayOfWeek,
    ];

    return workspaceCronParts.join(' ');
};
export const decodeHtmlEntities = (text: string): string =>
    text
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');

const toolNameToEnumKey: Record<string, AiToolFriendlyMessage> = {
    // Sortment tools (with sortment_ prefix)
    'sortment_beefree-tool': AiToolFriendlyMessage.SORTMENT_BEEFREE_TOOL,
    'sortment_get-srt-template':
        AiToolFriendlyMessage.SORTMENT_GET_SRT_TEMPLATE,
    'sortment_get-beefree-template':
        AiToolFriendlyMessage.SORTMENT_GET_BEEFREE_TEMPLATE,

    // Beefree structure tools
    beefree_beefree_get_content_hierarchy:
        AiToolFriendlyMessage.BEEFREE_GET_CONTENT_HIERARCHY,
    beefree_beefree_get_selected: AiToolFriendlyMessage.BEEFREE_GET_SELECTED,
    beefree_beefree_get_element_details:
        AiToolFriendlyMessage.BEEFREE_GET_ELEMENT_DETAILS,

    // Beefree section/column tools
    beefree_beefree_add_section: AiToolFriendlyMessage.BEEFREE_ADD_SECTION,
    beefree_beefree_update_section_style:
        AiToolFriendlyMessage.BEEFREE_UPDATE_SECTION_STYLE,
    beefree_beefree_update_column_style:
        AiToolFriendlyMessage.BEEFREE_UPDATE_COLUMN_STYLE,
    beefree_beefree_delete_element:
        AiToolFriendlyMessage.BEEFREE_DELETE_ELEMENT,

    // Beefree content add tools
    beefree_beefree_add_title: AiToolFriendlyMessage.BEEFREE_ADD_TITLE,
    beefree_beefree_add_paragraph: AiToolFriendlyMessage.BEEFREE_ADD_PARAGRAPH,
    beefree_beefree_add_button: AiToolFriendlyMessage.BEEFREE_ADD_BUTTON,
    beefree_beefree_add_image: AiToolFriendlyMessage.BEEFREE_ADD_IMAGE,
    beefree_beefree_add_list: AiToolFriendlyMessage.BEEFREE_ADD_LIST,
    beefree_beefree_add_divider: AiToolFriendlyMessage.BEEFREE_ADD_DIVIDER,
    beefree_beefree_add_spacer: AiToolFriendlyMessage.BEEFREE_ADD_SPACER,
    beefree_beefree_add_menu: AiToolFriendlyMessage.BEEFREE_ADD_MENU,
    beefree_beefree_add_social: AiToolFriendlyMessage.BEEFREE_ADD_SOCIAL,
    beefree_beefree_add_html: AiToolFriendlyMessage.BEEFREE_ADD_HTML,
    beefree_beefree_add_video: AiToolFriendlyMessage.BEEFREE_ADD_VIDEO,
    beefree_beefree_add_icons: AiToolFriendlyMessage.BEEFREE_ADD_ICONS,

    // Beefree content update tools
    beefree_beefree_update_title: AiToolFriendlyMessage.BEEFREE_UPDATE_TITLE,
    beefree_beefree_update_paragraph:
        AiToolFriendlyMessage.BEEFREE_UPDATE_PARAGRAPH,
    beefree_beefree_update_button: AiToolFriendlyMessage.BEEFREE_UPDATE_BUTTON,
    beefree_beefree_update_image: AiToolFriendlyMessage.BEEFREE_UPDATE_IMAGE,
    beefree_beefree_update_list: AiToolFriendlyMessage.BEEFREE_UPDATE_LIST,
    beefree_beefree_update_divider:
        AiToolFriendlyMessage.BEEFREE_UPDATE_DIVIDER,
    beefree_beefree_update_spacer: AiToolFriendlyMessage.BEEFREE_UPDATE_SPACER,
    beefree_beefree_update_menu: AiToolFriendlyMessage.BEEFREE_UPDATE_MENU,
    beefree_beefree_update_social: AiToolFriendlyMessage.BEEFREE_UPDATE_SOCIAL,
    beefree_beefree_update_html: AiToolFriendlyMessage.BEEFREE_UPDATE_HTML,
    beefree_beefree_update_video: AiToolFriendlyMessage.BEEFREE_UPDATE_VIDEO,
    beefree_beefree_update_icons: AiToolFriendlyMessage.BEEFREE_UPDATE_ICONS,

    // Beefree template management tools
    beefree_beefree_list_templates:
        AiToolFriendlyMessage.BEEFREE_LIST_TEMPLATES,
    beefree_beefree_clone_template:
        AiToolFriendlyMessage.BEEFREE_CLONE_TEMPLATE,
    beefree_beefree_check_template:
        AiToolFriendlyMessage.BEEFREE_CHECK_TEMPLATE,
    beefree_beefree_set_email_metadata:
        AiToolFriendlyMessage.BEEFREE_SET_EMAIL_METADATA,

    // Web fetch tool
    WebFetchSafe: AiToolFriendlyMessage.WEBFETCHSAFE,
    webfetchsafe: AiToolFriendlyMessage.WEBFETCHSAFE,
};

/**
 * Gets a human-friendly message for a given tool name.
 * @param toolName - The tool name from the AI service
 * @returns A user-friendly message describing what the tool is doing
 */
export const getToolFriendlyMessage = (
    toolName: string | null | undefined,
): string => {
    if (!toolName) return AiToolFriendlyMessage.DEFAULT;

    // Direct lookup
    if (toolName in toolNameToEnumKey) {
        return toolNameToEnumKey[toolName];
    }

    // Try lowercase lookup
    const lowerToolName = toolName.toLowerCase();
    if (lowerToolName in toolNameToEnumKey) {
        return toolNameToEnumKey[lowerToolName];
    }

    // Fallback: return default message
    return AiToolFriendlyMessage.DEFAULT;
};
